<template>
  <div
    class="share-access-table__row user-info"
    :class="{ 'user-info--mobile': $vuetify.breakpoint.width < 475 }"
  >
    <div class="share-access-table__col-person">
      <div class="user-info__person">
        <v-avatar size="32px" class="user-info__avatar">
          <v-img :src="getAvatarUrl(item.email, 48)" />
        </v-avatar>
        <div class="user-info__details">
          <div
            class="user-info__name primary-onhover"
            :class="
              $vuetify.breakpoint.smAndDown
                ? 'user-info__name--stacked'
                : 'user-info__name--inline'
            "
          >
            <span v-if="isYou" class="user-info__name-text" :title="ownerDisplayName">{{
              $t("table.field.userOwner", { name: item.name })
            }}</span>
            <span v-else class="user-info__name-text" :title="item.name">{{ item.name }}</span>

            <template v-if="item.invitationPending">
              <v-tooltip open-delay="150" bottom transition="custom-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <div v-on="on" v-bind="attrs" class="d-inline-flex">
                    <StatusLabel
                      small
                      :xsmall="true"
                      :status="'info'"
                      :value="$t('message.status.pending')"
                      class="ml-2 user-info__name-badge"
                    />
                  </div>
                </template>
                <div class="d-flex align-center">
                  <v-icon size="16" class="mr-2">$hourglassIcon</v-icon>
                  {{
                    $t("tooltip.sharedAccess.invited", {
                      time: item.invited_at_text,
                    })
                  }}
                </div>
              </v-tooltip>

              <v-tooltip open-delay="150" bottom transition="custom-tooltip">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-on="on"
                    v-bind="attrs"
                    v-if="item.invitationPending"
                    text
                    class="user-info__resend"
                    :class="hasPendingChanges ? 'error--text' : 'info-onhover'"
                    @click="$emit('resend-button-clicked', item)"
                    :loading="resending"
                    icon
                    small
                    :disabled="!resendAvailable"
                  >
                    <v-icon size="16">$restore</v-icon>
                  </v-btn>
                </template>
                <span>{{ resendButtonTooltipText }}</span>
              </v-tooltip>
            </template>
          </div>
          <p
            v-if="item.name !== item.email"
            class="user-info__email p-3 mb-0 primary-onhover text-truncate"
            :title="item.email"
          >
            {{ item.email }}
          </p>
        </div>
      </div>
    </div>

    <div class="share-access-table__col-role">
      <select-dropdown
        :outlined="true"
        :value="item.role"
        :itemText="'text'"
        :itemValue="'key'"
        :items="roles"
        :taggable="true"
        :size="'lg'"
        :block="true"
        :placeholder="$t('heading.instance.modal.sharedAccess.selectRole')"
        class="filter-select--role-access"
        @update:value="handleAccessSettingChange"
      />
      <div v-if="mixedRoles" class="user-mixed-role p-xxs mt-1">
        {{ $t("heading.instance.modal.sharedAccess.mixedTole.title") }}
      </div>
    </div>

    <div
      v-if="showSsoColumn"
      class="share-access-table__col-login-as"
    >
      <select-dropdown
        v-if="showWordpressUserPicker"
        :key="wordpressUserSelectKey"
        :outlined="true"
        :value="wordpressUserSelectValue"
        :itemText="'text'"
        :itemValue="'key'"
        :items="wordpressUserDropdownItems"
        :return-object="false"
        raw-item-text
        :size="'lg'"
        :block="true"
        :placeholder="$t('form.label.loginAs')"
        class="filter-select--login-as"
        @update:value="handleWordpressUserChange"
      />
    </div>

    <div class="share-access-table__col-actions">
      <v-tooltip open-delay="150" bottom transition="custom-tooltip">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-on="on"
            v-bind="attrs"
            @click.stop="removeAccess"
            small
            icon
            class="error-onhover"
          >
            <v-icon size="20">$trash</v-icon>
          </v-btn>
        </template>
        {{ $t("tooltip.sharedAccess.removeAccess") }}
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import SelectDropdown from "../SelectDropdown.vue";
import StatusLabel from "../StatusLabel.vue";
import { getAvatarUrl } from "@/apis/HelperFunctions";
import moment from "moment";

export default {
  emits: ["role-changed", "wordpress-user-changed", "user-remove-access"],
  components: {
    SelectDropdown,
    StatusLabel,
  },
  props: {
    roles: Array,
    item: Object,
    hasPendingChanges: Boolean,
    createShare: {
      type: Boolean,
      default: false,
    },
    mixedRoles: {
      type: Boolean,
      default: false,
    },
    resending: {
      type: Boolean,
      default: false,
    },
    wordpressUserDropdownItems: {
      type: Array,
      default: () => [],
    },
    showSsoColumn: {
      type: Boolean,
      default: false,
    },
    showWordpressUserPicker: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      getAvatarUrl,
    };
  },
  computed: {
    wordpressUserSelectValue() {
      const id = this.item.wordpress_user_id;
      if (id == null || id === "") {
        return 0;
      }
      const normalized = Number(id);
      return Number.isNaN(normalized) ? 0 : normalized;
    },
    wordpressUserSelectKey() {
      return `wp-user-${this.wordpressUserDropdownItems.length}-${this.wordpressUserSelectValue}`;
    },
    resendAvailable() {
      return (
        this.item.resend_available_at &&
        moment(this.item.resend_available_at).isBefore()
      );
    },
    resendButtonTooltipText() {
      if (this.resendAvailable) {
        return this.$t("tooltip.sharedAccess.resend");
      }
      return this.$t("tooltip.sharedAccess.resendNotAvailable", {
        email: this.item.email,
        time: moment(this.item.resend_available_at).fromNow(),
      });
    },
    isYou() {
      return this.$store.state.home.user.email == this.item.email;
    },
    ownerDisplayName() {
      if (!this.isYou) {
        return this.item.name;
      }
      return this.$t("table.field.userOwner", { name: this.item.name });
    },
  },
  methods: {
    removeAccess: function () {
      this.$emit("user-remove-access", this.item);
    },
    handleAccessSettingChange: function (event) {
      this.$emit("role-changed", this.item, event.key);
    },
    handleWordpressUserChange: function (value) {
      if (value == null || value === "") {
        this.$emit("wordpress-user-changed", this.item, null);
        return;
      }
      const id = typeof value === "object" ? value.key : value;
      if (Number(id) === 0) {
        this.$emit("wordpress-user-changed", this.item, null);
        return;
      }
      this.$emit("wordpress-user-changed", this.item, Number(id));
    },
  },
};
</script>

<style lang="scss" scoped>
.share-access-table__row {
  display: grid;
  grid-template-columns: var(--share-access-grid-columns);
  gap: 8px 16px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid var(--v-gray-lighten4);
  transition: background-color 0.24s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--v-panel-lighten1);
  }

  &.user-info--mobile {
    @media (max-width: 475px) {
      grid-template-columns: 1fr;
      gap: 12px;

      .share-access-table__col-actions {
        justify-content: flex-end;
      }
    }
  }
}

.share-access-table__col-person {
  min-width: 0;
  overflow: hidden;
  padding-right: 4px;
}

.share-access-table__col-role,
.share-access-table__col-login-as {
  min-width: 0;
}

.filter-select--role-access::v-deep,
.filter-select--login-as::v-deep {
  .v-input {
    max-width: 100% !important;
    width: 100%;
  }
}

.share-access-table__col-actions {
  display: flex;
  justify-content: center;
}

.user-info {
  transition: background-color 0.24s ease;

  .primary-onhover {
    transition: color 0.24s ease;
  }

  &__person {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    overflow: hidden;
    width: 100%;
  }

  &__avatar {
    flex-shrink: 0;
    border: 1px solid var(--v-gray-lighten2) !important;
  }

  &__details {
    min-width: 0;
    margin-left: 12px;
    overflow: hidden;
    flex: 1 1 auto;
  }

  &__name {
    font-weight: $font-weight-bold;
    color: var(--v-gray-darken2);
    font-size: $font-size-sm;
    line-height: $line-height-sm;
    min-width: 0;

    &--inline {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      gap: 4px;
      min-width: 0;
    }

    &--stacked {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      min-width: 0;
    }
  }

  &__name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__email {
    color: var(--v-gray-darken1);
    margin-top: 2px;
    min-width: 0;
  }

  &__resend {
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;
    padding: 0 !important;
    margin-left: 2px;
    flex-shrink: 0;
  }

  &__name-badge {
    flex-shrink: 0;
  }
}

.user-mixed-role {
  min-width: fit-content !important;
  color: var(--v-gray-lighten1);
}

.error-onhover:hover {
  color: var(--v-error-base);
}

.info-onhover:hover {
  color: var(--v-info-base);
}
</style>
